import {
  createContext,
  useCallback,
  useContext,
} from "react";
import type { ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSessionProbe } from "./useSessionProbe.js";
import { logout as apiLogout } from "../api/lkAuth.js";
import { invokeOnUnauthorized } from "../api/client.js";
import type { MeResponse } from "../types/user.js";

export interface AuthContextValue {
  me: MeResponse | undefined;
  refresh: () => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Provides the current session to the whole app tree.
 * Must be rendered inside a <QueryClientProvider>.
 *
 * Exposes:
 *   me       — the authenticated user (undefined while loading or logged out)
 *   refresh  — force-refetch /lk/me
 *   logout   — POST logout, invalidate ["me"], invoke the onUnauthorized redirect
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const { data: me, refresh } = useSessionProbe();
  const queryClient = useQueryClient();

  const logout = useCallback(async () => {
    try {
      await apiLogout();
    } catch {
      // Ignore errors — we're logging out regardless
    }
    await queryClient.invalidateQueries({ queryKey: ["me"] });
    invokeOnUnauthorized();
  }, [queryClient]);

  return (
    <AuthContext.Provider value={{ me, refresh, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

/** Hook for consuming the auth context. Must be called inside <AuthProvider>. */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be called inside <AuthProvider>");
  return ctx;
}
